import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, ClipboardList, Sparkles, Check } from "lucide-react";
import FadeUp from "../components/site/FadeUp";
import CTASection from "../components/site/CTASection";
import { SERVICES } from "../data/site";
import { track, EVENTS } from "../lib/analytics";

const STEPS = [
  {
    icon: CalendarCheck,
    title: "Book a walkthrough",
    body: "A free, in-home visit. We listen to what's overwhelming you and see how your household really runs.",
  },
  {
    icon: ClipboardList,
    title: "We build your custom plan",
    body: "A plan shaped around your home and your week — the right tasks, the right rhythm, priced plainly.",
  },
  {
    icon: Sparkles,
    title: "Your team comes weekly",
    body: "A consistent, trusted team resets your home each week. Over time, they just know how you like things.",
  },
];

export default function HowItWorks() {
  useEffect(() => {
    track(EVENTS.PRICING_VIEW, { page: "how-it-works" });
  }, []);

  return (
    <div data-testid="how-it-works-page">
      <section className="section-pad">
        <div className="container-hh max-w-3xl">
          <FadeUp>
            <p className="eyebrow">How it works &amp; pricing</p>
            <h1 className="mt-3 text-4xl md:text-5xl lg:text-6xl">
              Simple to start. Built to last.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-bodytext">
              Weekly Home Management is a standing relationship, not a one-off
              clean. It starts with a conversation, becomes a plan, and settles
              into a rhythm that quietly gives you your time back.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="section-pad bg-seafoam">
        <div className="container-hh">
          <div className="grid gap-8 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.1}>
                <div className="cozy-card h-full p-8">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-seafoam">
                    <s.icon className="h-6 w-6 text-teal" />
                  </div>
                  <p className="mt-5 text-sm font-semibold text-teal">Step {i + 1}</p>
                  <h3 className="mt-1 text-2xl">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-bodytext">{s.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing table */}
      <section data-testid="pricing-table" className="section-pad">
        <div className="container-hh">
          <FadeUp className="max-w-2xl">
            <p className="eyebrow">Transparent pricing</p>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl">
              What our services cost
            </h2>
            <p className="mt-4 text-bodytext">
              No hidden fees, no surprises. Project services are quoted up front
              after we understand your home.
            </p>
          </FadeUp>

          <div className="mt-10 space-y-4">
            {SERVICES.map((s, i) => {
              const featured = s.slug === "weekly-home-concierge";
              return (
                <FadeUp key={s.slug} delay={Math.min(i * 0.05, 0.25)}>
                  <div
                    data-testid={`pricing-row-${s.slug}`}
                    className={`flex flex-col gap-4 rounded-[12px] p-6 md:flex-row md:items-center md:justify-between ${
                      featured
                        ? "bg-navy text-white shadow-cozy-lg"
                        : "bg-white shadow-cozy"
                    }`}
                  >
                    <div className="md:w-1/3">
                      <div className="flex items-center gap-3">
                        <h3
                          className={`text-2xl ${featured ? "text-white" : ""}`}
                        >
                          {s.title}
                        </h3>
                        {featured && (
                          <span className="rounded-full bg-teal px-3 py-1 text-xs font-semibold text-white">
                            Most popular
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="md:w-1/3">
                      <p
                        className={`font-serif text-2xl ${
                          featured ? "text-seafoamlight" : "text-teal"
                        }`}
                      >
                        {s.price}
                      </p>
                      <p
                        className={`mt-1 text-sm ${
                          featured ? "text-white/70" : "text-bodytext/70"
                        }`}
                      >
                        {s.priceNote}
                      </p>
                    </div>
                    <div className="md:w-1/4 md:text-right">
                      <Link
                        to={s.ctaType === "book" ? "/book" : s.path}
                        data-testid={`pricing-cta-${s.slug}`}
                        className={
                          featured
                            ? "btn-teal bg-teal hover:bg-white hover:text-navy"
                            : "btn-outline-navy"
                        }
                      >
                        {s.cta}
                      </Link>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>

          <FadeUp className="mt-8 flex items-start gap-3 rounded-[12px] bg-seafoam p-6">
            <Check className="mt-0.5 h-5 w-5 flex-none text-teal" />
            <p className="text-sm text-bodytext">
              Weekly Home Management is billed monthly with an initial 3-month
              commitment. For Vacation Provisions, any shopping is billed
              separately at cost.
            </p>
          </FadeUp>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
